// src/components/LicenseFilters/LicenseFilters.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
    InputAdornment,
    Button,
    Typography,
    IconButton,
    Tooltip,
    Checkbox,
    ListItemText,
    ListSubheader,
    Link,
    Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import translationEn from '../../i18n/supported_languages/en/translation.json';
// Reuse HistoryFilters CSS for consistency
import '../../components/HistoryFilters/HistoryFilters.css'; 

const LicenseFilters = ({ filters, onFilterChange, onResetFilters, onClose, filenameOptions = [] }) => {
    const { t } = useTranslation();

    // --- CONSTANTS ---
    const STATUS_OPTIONS = [
        { key: 'active', name: t('licenseFilters.status.options.active') },
        { key: 'expiring', name: t('licenseFilters.status.options.expiring') },
        { key: 'expired', name: t('licenseFilters.status.options.expired') },
        { key: 'inactive', name: t('licenseFilters.status.options.inactive') }
    ];

    const TYPE_OPTIONS = [
        { key: 'image', name: t('licenseFilters.type.options.image') },
        { key: 'video', name: t('licenseFilters.type.options.video') },
        { key: 'audio', name: t('licenseFilters.type.options.audio') },
        { key: 'text', name: t('licenseFilters.type.options.text') }
    ];

    const CATEGORY_OPTIONS = Object.keys(translationEn.contentMetadata.inclusion).map(key => ({
        key: key,
        name: t(`contentMetadata.inclusion.${key}`)
    })).sort((a, b) => a.name.localeCompare(b.name));

    const SAFETY_FLAG_OPTIONS = Object.keys(translationEn.contentMetadata.exclusion).map(key => ({
        key: key,
        name: t(`contentMetadata.exclusion.${key}`)
    })).sort((a, b) => a.name.localeCompare(b.name));

    // --- HANDLERS ---
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        onFilterChange(name, value);
    };

    const handleMultiSelectChange = (event) => {
        const { name, value } = event.target;
        onFilterChange(name, typeof value === 'string' ? value.split(',') : value);
    };

    const handleSelectAll = (filterName, allOptions) => {
        onFilterChange(filterName, allOptions.map(opt => opt.key));
    };

    const handleClearAll = (filterName) => {
        onFilterChange(filterName, []);
    };

    // --- ACTIVE STATE CHECKS ---
    // 1. Search
    const isSearchActive = !!filters.searchQuery;

    // 2. Status (Active if subset is selected)
    const isStatusFilterActive = filters.status.length > 0 && filters.status.length < STATUS_OPTIONS.length;

    // 3. Type (Active if subset is selected)
    const isTypeFilterActive = filters.type.length > 0 && filters.type.length < TYPE_OPTIONS.length;

    // 4. Safety Flags (Active if ANY are selected, assuming default is none)
    const isSafetyActive = filters.safetyFlags && filters.safetyFlags.length > 0;
    const safetyLabel = t('historyFilters.safetyFlags.labelWithCount', { 
        count: filters.safetyFlags ? filters.safetyFlags.length : 0,
        defaultValue: `Safety Flags (${filters.safetyFlags ? filters.safetyFlags.length : 0})`
    });

    // 5. Content Category (Active if subset is selected)
    const isCategoryActive = filters.contentCategories && filters.contentCategories.length < CATEGORY_OPTIONS.length;
    const categoryLabel = t('historyFilters.contentCategory.labelWithCount', { 
        count: filters.contentCategories ? filters.contentCategories.length : 0,
        defaultValue: `Content Category (${filters.contentCategories ? filters.contentCategories.length : 0})`
    });

    // 6 & 7. Date Range
    const todayString = new Date().toISOString().split('T')[0];

    return (
        <div className="filter-drawer-container">
            <header className="filter-drawer-header">
                <Typography variant="h6" component="h3">{t('licenseFilters.title')}</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </header>

            <div className="filter-drawer-body">
                
                {/* 1) Search Filter */}
                <div className={`filter-item-wrapper ${isSearchActive ? 'active-filter' : ''}`}>
                    <Autocomplete
                        freeSolo
                        fullWidth
                        options={filenameOptions}
                        value={filters.searchQuery}
                        // Handle selecting an option from the list
                        onChange={(event, newValue) => {
                            onFilterChange('searchQuery', newValue || '');
                        }}
                        // Handle typing directly
                        onInputChange={(event, newInputValue) => {
                            onFilterChange('searchQuery', newInputValue || '');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('licenseFilters.search.label')}
                                variant="outlined"
                                className="filter-text-field"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                    {isSearchActive && (
                        <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={() => onFilterChange('searchQuery', '')} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* 2) License Status Filter */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isStatusFilterActive ? 'active-filter' : ''}`}>
                         <InputLabel>{t('licenseFilters.status.label')}</InputLabel>
                        <Select
                            name="status"
                            multiple
                            value={filters.status}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={t('licenseFilters.status.label')} />}
                            renderValue={(selected) => {
                                if (selected.length === 0) return t('licenseFilters.status.all');
                                if (selected.length === STATUS_OPTIONS.length) return t('licenseFilters.status.all');
                                return selected
                                    .map(key => STATUS_OPTIONS.find(opt => opt.key === key)?.name)
                                    .join(', ');
                            }}
                            MenuProps={{ className: 'history-multi-select-menu' }}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={filters.status.indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isStatusFilterActive && (
                        <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                            <IconButton 
                                className="single-reset-btn" 
                                onClick={() => onFilterChange('status', STATUS_OPTIONS.map(o => o.key))} 
                                size="small"
                            >
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* 3) Content Type Filter */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isTypeFilterActive ? 'active-filter' : ''}`}>
                        <InputLabel>{t('licenseFilters.type.label')}</InputLabel>
                        <Select
                            name="type"
                            multiple
                            value={filters.type}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={t('licenseFilters.type.label')} />}
                            renderValue={(selected) => {
                                if (selected.length === TYPE_OPTIONS.length) return t('licenseFilters.type.all');
                                return selected
                                    .map(key => TYPE_OPTIONS.find(opt => opt.key === key)?.name)
                                    .join(', ');
                            }}
                            MenuProps={{ className: 'history-multi-select-menu' }}
                        >
                            {TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={filters.type.indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTypeFilterActive && (
                        <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                            <IconButton 
                                className="single-reset-btn" 
                                onClick={() => onFilterChange('type', TYPE_OPTIONS.map(o => o.key))} 
                                size="small"
                            >
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* 4) Safety Flags Filter */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isSafetyActive ? 'active-filter' : ''}`}>
                        <InputLabel shrink>{safetyLabel}</InputLabel>
                        <Select
                            multiple
                            displayEmpty
                            name="safetyFlags"
                            value={filters.safetyFlags}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={safetyLabel} />}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return (
                                        <span style={{ fontStyle: 'normal' }}>
                                            {t('historyFilters.safetyFlags.noFlagsSelected')}
                                        </span>
                                    );
                                }
                                return selected.map(s => t(`contentMetadata.exclusion.${s}`)).join(', ');
                            }}
                            MenuProps={{ 
                                className: 'history-multi-select-menu',
                                autoFocus: false,
                                PaperProps: { style: { maxHeight: 300 } },
                            }}
                        >
                            <ListSubheader>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="overline">{t('historyFilters.actions')}</Typography>
                                    <Box>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleSelectAll('safetyFlags', SAFETY_FLAG_OPTIONS)}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('historyFilters.selectAll')}
                                        </Link>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleClearAll('safetyFlags')}
                                        >
                                            {t('historyFilters.clearAll')}
                                        </Link>
                                    </Box>
                                </Box>
                            </ListSubheader>
                            {SAFETY_FLAG_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={(filters.safetyFlags || []).indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isSafetyActive && (
                        <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={() => onFilterChange('safetyFlags', [])} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* 5) Content Category Filter */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isCategoryActive ? 'active-filter' : ''}`}>
                        <InputLabel>{categoryLabel}</InputLabel>
                        <Select
                            multiple
                            name="contentCategories"
                            value={filters.contentCategories || []}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={categoryLabel} />}
                            renderValue={(selected) => {
                                if (selected.length === CATEGORY_OPTIONS.length) {
                                    return t('licenseFilters.contentCategory.allContentCategories', t('historyFilters.contentCategory.allContentCategories'));
                                }
                                return selected
                                    .map(s => t(`contentMetadata.inclusion.${s}`))
                                    .join(', ');
                            }}
                            MenuProps={{ 
                                className: 'history-multi-select-menu',
                                autoFocus: false,
                                PaperProps: { style: { maxHeight: 300 } },
                            }}
                        >
                            <ListSubheader>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="overline">{t('historyFilters.actions')}</Typography>
                                    <Box>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleSelectAll('contentCategories', CATEGORY_OPTIONS)}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('historyFilters.selectAll')}
                                        </Link>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleClearAll('contentCategories')}
                                        >
                                            {t('historyFilters.clearAll')}
                                        </Link>
                                    </Box>
                                </Box>
                            </ListSubheader>
                            {CATEGORY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={(filters.contentCategories || []).indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isCategoryActive && (
                        <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                            <IconButton 
                                className="single-reset-btn" 
                                onClick={() => onFilterChange('contentCategories', CATEGORY_OPTIONS.map(o => o.key))}
                                size="small"
                            >
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* 6 & 7) Date Range Filter */}
                <Box className="date-range-filter">
                    <div className={`filter-item-wrapper ${filters.startDate ? 'active-filter' : ''}`}>
                        <TextField
                            name="startDate"
                            label={t('historyFilters.dateRange.from')}
                            type="date"
                            value={filters.startDate || ''}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            className="filter-text-field"
                            inputProps={{
                                max: filters.endDate || todayString
                            }}
                        />
                        {filters.startDate && (
                            <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                                <IconButton className="single-reset-btn" onClick={() => onFilterChange('startDate', '')} size="small">
                                    <ReplayIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                    <div className={`filter-item-wrapper ${filters.endDate ? 'active-filter' : ''}`}>
                        <TextField
                            name="endDate"
                            label={t('historyFilters.dateRange.to')}
                            type="date"
                            value={filters.endDate || ''}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            className="filter-text-field"
                            inputProps={{
                                min: filters.startDate,
                                max: todayString
                            }}
                        />
                        {filters.endDate && (
                            <Tooltip title={t('licenseFilters.resetTooltip')} arrow>
                                <IconButton className="single-reset-btn" onClick={() => onFilterChange('endDate', '')} size="small">
                                    <ReplayIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </Box>

            </div>

            <div className="reset-button-container">
                <Button variant="text" onClick={onResetFilters} className="reset-filters-btn">
                    {t('licenseFilters.resetAll')}
                </Button>
            </div>
        </div>
    );
};

export default LicenseFilters;